import React, { useState } from 'react';
import {
  useIngredientes,
  useCreateIngrediente,
  useReactivarIngrediente,
} from '../hooks/useIngredientes';
import { useProveedores, useCrearProveedor } from '../hooks/useProveedores';
import type { Proveedor } from '../services/proveedoresAPI';
import ordenesCompraAPI from '../services/ordenesCompraAPI';
import ErrorModal from './ErrorModal';
import {
  ShoppingCart,
  User,
  Package,
  FileText,
  ChevronLeft,
  ChevronRight,
  X,
  Plus,
  Minus,
  Search,
} from 'lucide-react';

// Interfaces para el wizard
interface OrdenCompraFormData {
  // Paso 1: Proveedor
  proveedor: {
    id?: number;
    nombre: string;
    contacto?: string;
    email?: string;
    telefono?: string;
    esNuevo?: boolean;
    rut?: string;
    categoria?: string;
    direccion?: string;
  };
  // Paso 2: Items
  items: {
    id: string;
    ingredienteId: string;
    ingredienteNombre: string;
    unidadMedida: string;
    cantidad: number;
    precioUnitario: number;
    subtotal: number;
  }[];
  // Paso 3: Detalles finales
  observaciones: string;
  fechaEntregaEsperada?: string;
}

interface WizardOrdenCompraProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: OrdenCompraFormData) => Promise<void>;
  ordenEditar?: any; // Para futuras ediciones
}

const WizardOrdenCompra: React.FC<WizardOrdenCompraProps> = ({
  isOpen,
  onClose,
  onSubmit,
  ordenEditar,
}) => {
  // Estados del wizard
  const [pasoActual, setPasoActual] = useState(1);
  const totalPasos = 3;
  const [loading, setLoading] = useState(false);

  // Estados del formulario
  const [formData, setFormData] = useState<OrdenCompraFormData>({
    proveedor: {
      nombre: '',
      esNuevo: false,
    },
    items: [],
    observaciones: '',
    fechaEntregaEsperada: '',
  });

  // Estados para búsqueda de ingredientes
  const [busquedaIngrediente, setBusquedaIngrediente] = useState('');
  // Versión debounced del término de búsqueda — se manda al backend tras 250ms
  // de inactividad para evitar disparar una request por cada tecla.
  const [busquedaIngredienteDebounced, setBusquedaIngredienteDebounced] =
    useState('');
  React.useEffect(() => {
    const timer = setTimeout(
      () => setBusquedaIngredienteDebounced(busquedaIngrediente),
      250
    );
    return () => clearTimeout(timer);
  }, [busquedaIngrediente]);
  const [mostrarFormIngrediente, setMostrarFormIngrediente] = useState(false);
  const [nuevoIngrediente, setNuevoIngrediente] = useState({
    nombre: '',
    descripcion: '',
    categoria: 'otros',
    unidad_medida: 'unidad',
    costo_unitario: 0,
    stock_actual: 0,
    stock_minimo: 0,
  });
  // El listado del buscador se filtra server-side con `search` para que
  // alcance todos los ingredientes (no sólo la primera página paginada).
  // Cuando el usuario aún no escribió nada, no pedimos un listado masivo —
  // limit=50 es suficiente como precarga.
  const { data: ingredientesData } = useIngredientes({
    search: busquedaIngredienteDebounced.trim() || undefined,
    limit: 50,
  });
  const ingredientesDisponibles = ingredientesData?.ingredientes || [];
  const crearIngredienteMutation = useCreateIngrediente();
  // Note: useBuscarSimilares requiere un parámetro, se usará en el manejo de errores específico
  const reactivarIngredienteMutation = useReactivarIngrediente();

  // Estado para ingredientes filtrados por proveedor
  const [ingredientesProveedor, setIngredientesProveedor] = useState<any[]>([]);
  const [cargandoIngredientesProveedor, setCargandoIngredientesProveedor] =
    useState(false);

  // Paginación para ingredientes del proveedor
  const [paginaIngredientesProveedor, setPaginaIngredientesProveedor] =
    useState(1);
  const ingredientesPorPagina = 4;

  // Estados para manejo de errores de ingredientes similares
  const [errorModal, setErrorModal] = useState<{
    open: boolean;
    type: 'simple' | 'similar' | 'exacto' | 'exactoActivo';
    message: string;
    ingredientesSimilares?: any[];
    ingredienteExacto?: any | null;
  }>({
    open: false,
    type: 'simple',
    message: '',
    ingredientesSimilares: [],
    ingredienteExacto: null,
  });

  // Estado para guardar los datos del ingrediente que se intenta crear
  const [ingredienteDataPendiente, setIngredienteDataPendiente] = useState<
    any | null
  >(null);

  // Estados para categorías dinámicas
  const [categorias, setCategorias] = useState([
    { value: 'carnes', label: 'Carnes' },
    { value: 'vegetales', label: 'Vegetales' },
    { value: 'lacteos', label: 'Lácteos' },
    { value: 'cereales', label: 'Cereales' },
    { value: 'condimentos', label: 'Condimentos' },
    { value: 'otros', label: 'Otros' },
  ]);
  const [mostrandoInputCategoria, setMostrandoInputCategoria] = useState(false);
  const [nuevaCategoria, setNuevaCategoria] = useState('');

  // Estados y hooks para proveedores
  const [mostrarFormProveedor, setMostrarFormProveedor] = useState(false);
  const [proveedorSeleccionado, setProveedorSeleccionado] =
    useState<Proveedor | null>(null);
  const [busquedaProveedor, setBusquedaProveedor] = useState('');
  const [paginaProveedores, setPaginaProveedores] = useState(1);
  const proveedoresPorPagina = 10; // Usar el límite del backend

  // Usar el hook con parámetros de búsqueda y paginación
  const { data: proveedoresData, isLoading: cargandoProveedores } =
    useProveedores({
      search: busquedaProveedor || undefined,
      page: paginaProveedores,
      limit: proveedoresPorPagina,
      activo: true, // Solo proveedores activos
    });

  const crearProveedorMutation = useCrearProveedor();
  const proveedoresDisponibles = proveedoresData?.data || [];
  const totalProveedores = proveedoresData?.pagination?.total || 0;
  const totalPaginasProveedores = proveedoresData?.pagination?.totalPages || 1;

  // Resetear paginación cuando cambia la búsqueda
  React.useEffect(() => {
    setPaginaProveedores(1);
  }, [busquedaProveedor]);

  const pasos = [
    { numero: 1, titulo: 'Proveedor', icono: User },
    { numero: 2, titulo: 'Ingredientes', icono: Package },
    { numero: 3, titulo: 'Resumen', icono: FileText },
  ];

  // Cargar ingredientes cuando se selecciona un proveedor
  React.useEffect(() => {
    const cargarIngredientesProveedor = async () => {
      if (formData.proveedor.id && !formData.proveedor.esNuevo) {
        try {
          setCargandoIngredientesProveedor(true);
          const response =
            await ordenesCompraAPI.obtenerIngredientesPorProveedor(
              formData.proveedor.id
            );
          if (response.success) {
            setIngredientesProveedor(response.data);
            setPaginaIngredientesProveedor(1); // Resetear paginación
          }
        } catch (error) {
          console.error('Error al cargar ingredientes del proveedor:', error);
          setIngredientesProveedor([]);
        } finally {
          setCargandoIngredientesProveedor(false);
        }
      } else {
        setIngredientesProveedor([]);
        setPaginaIngredientesProveedor(1);
      }
    };

    cargarIngredientesProveedor();
  }, [formData.proveedor.id, formData.proveedor.esNuevo]);

  // Función para formatear moneda con separadores de miles
  const formatearMoneda = (cantidad: number): string => {
    return new Intl.NumberFormat('es-CL', {
      style: 'currency',
      currency: 'CLP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(cantidad);
  };

  // Función para calcular totales con desglose
  const calcularTotalesConDesglose = () => {
    const subtotal = formData.items.reduce(
      (sum, item) => sum + item.subtotal,
      0
    );
    const impuesto = subtotal * 0.19; // 19% IVA
    const total = subtotal + impuesto;

    return {
      subtotal,
      impuesto,
      total,
    };
  };

  // Funciones auxiliares para el manejo de errores de ingredientes similares
  const limpiarFormularioIngrediente = () => {
    setNuevoIngrediente({
      nombre: '',
      descripcion: '',
      categoria: 'otros',
      unidad_medida: 'unidad',
      costo_unitario: 0,
      stock_actual: 0,
      stock_minimo: 0,
    });
    setMostrarFormIngrediente(false);
    setIngredienteDataPendiente(null);
    // Resetear estado de categorías
    setMostrandoInputCategoria(false);
    setNuevaCategoria('');
  };

  // Funciones para manejar categorías dinámicas
  const agregarNuevaCategoria = () => {
    if (nuevaCategoria.trim()) {
      const valorCategoria = nuevaCategoria.toLowerCase().replace(/\s+/g, '_');
      const nuevaCategoriaObj = {
        value: valorCategoria,
        label: nuevaCategoria.trim(),
      };

      setCategorias((prev) => [...prev, nuevaCategoriaObj]);
      setNuevoIngrediente((prev) => ({
        ...prev,
        categoria: valorCategoria,
      }));

      setNuevaCategoria('');
      setMostrandoInputCategoria(false);
    }
  };

  const cancelarNuevaCategoria = () => {
    setNuevaCategoria('');
    setMostrandoInputCategoria(false);
  };

  const agregarIngredienteAOrden = (ingrediente: any) => {
    const nuevoItem = {
      id: Date.now().toString(),
      ingredienteId: ingrediente.id.toString(),
      ingredienteNombre: ingrediente.nombre,
      unidadMedida: ingrediente.unidad_medida || 'unidad',
      cantidad: 1,
      precioUnitario: ingrediente.costo_unitario || 0,
      subtotal: ingrediente.costo_unitario || 0,
    };

    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, nuevoItem],
    }));
  };

  const handleReactivarIngrediente = async (ingrediente: any) => {
    try {
      const ingredienteReactivado =
        await reactivarIngredienteMutation.mutateAsync({
          id: ingrediente.id,
          stockActual: ingrediente.stock_actual || 0,
        });
      agregarIngredienteAOrden(ingredienteReactivado);
      limpiarFormularioIngrediente();
      setErrorModal((prev) => ({ ...prev, open: false }));
    } catch (error) {
      console.error('Error al reactivar ingrediente:', error);
      setErrorModal((prev) => ({
        ...prev,
        message:
          'Error al reactivar el ingrediente. Por favor, inténtalo de nuevo.',
      }));
    }
  };

  const handleSeleccionarSimilar = (ingrediente: any) => {
    agregarIngredienteAOrden(ingrediente);
    limpiarFormularioIngrediente();
    setErrorModal((prev) => ({ ...prev, open: false }));
  };

  const handleContinuarDeTodasFormas = async () => {
    if (!ingredienteDataPendiente) {
      return;
    }

    try {
      // Agregar un flag para indicar que queremos continuar de todas formas
      const dataConFlag = {
        ...ingredienteDataPendiente,
        forzarCreacion: true,
        // Agregar proveedor como string si está disponible
        ...(formData.proveedor.nombre && {
          proveedor: formData.proveedor.nombre,
        }),
      };

      const nuevoIngredienteCreado =
        await crearIngredienteMutation.mutateAsync(dataConFlag);

      agregarIngredienteAOrden(nuevoIngredienteCreado);
      limpiarFormularioIngrediente();
      setErrorModal((prev) => ({ ...prev, open: false }));
      setIngredienteDataPendiente(null);
    } catch (error) {
      console.error('Error al crear ingrediente forzadamente:', error);
      setErrorModal((prev) => ({
        ...prev,
        message:
          'Error al crear el ingrediente. Por favor, inténtalo de nuevo.',
      }));
    }
  };

  // Validaciones por paso
  const validarPaso = (paso: number): boolean => {
    switch (paso) {
      case 1:
        if (formData.proveedor.esNuevo) {
          // Validación para nuevo proveedor
          return (
            formData.proveedor.nombre.trim() !== '' &&
            formData.proveedor.rut?.trim() !== '' &&
            formData.proveedor.email?.trim() !== '' &&
            formData.proveedor.categoria?.trim() !== ''
          );
        } else {
          // Validación para proveedor existente
          return (
            formData.proveedor.id !== undefined &&
            formData.proveedor.nombre.trim() !== ''
          );
        }
      case 2:
        return formData.items.length > 0;
      case 3:
        return true; // El paso 3 es solo resumen
      default:
        return false;
    }
  };

  // Navegación
  const siguientePaso = () => {
    if (pasoActual < totalPasos && validarPaso(pasoActual)) {
      setPasoActual(pasoActual + 1);
    }
  };

  const pasoAnterior = () => {
    if (pasoActual > 1) {
      setPasoActual(pasoActual - 1);
    }
  };

  // Manejo de items
  const agregarItem = (ingrediente: any) => {
    const nuevoItem = {
      id: Date.now().toString(),
      ingredienteId: ingrediente.id,
      ingredienteNombre: ingrediente.nombre,
      unidadMedida: ingrediente.unidad_medida || 'unidad',
      cantidad: 1,
      precioUnitario: 0,
      subtotal: 0,
    };
    setFormData((prev) => ({
      ...prev,
      items: [...prev.items, nuevoItem],
    }));
  };

  const actualizarItem = (itemId: string, campo: string, valor: number) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.map((item) => {
        if (item.id === itemId) {
          const itemActualizado = { ...item, [campo]: valor };
          if (campo === 'cantidad' || campo === 'precioUnitario') {
            itemActualizado.subtotal =
              itemActualizado.cantidad * itemActualizado.precioUnitario;
          }
          return itemActualizado;
        }
        return item;
      }),
    }));
  };

  const eliminarItem = (itemId: string) => {
    setFormData((prev) => ({
      ...prev,
      items: prev.items.filter((item) => item.id !== itemId),
    }));
  };

  // Cálculos
  const calcularTotal = () => {
    return formData.items.reduce((total, item) => total + item.subtotal, 0);
  };

  // Envío del formulario
  const handleSubmit = async () => {
    if (!validarPaso(pasoActual)) return;

    // Validar fecha de entrega
    if (formData.fechaEntregaEsperada) {
      const fechaSeleccionada = new Date(formData.fechaEntregaEsperada);
      const hoy = new Date();
      hoy.setHours(0, 0, 0, 0); // Resetear las horas para comparar solo fechas

      if (fechaSeleccionada < hoy) {
        alert('La fecha de entrega no puede ser anterior a hoy');
        return;
      }
    }

    setLoading(true);
    try {
      let datosOrden = { ...formData };

      // Si es un proveedor nuevo, crearlo primero
      if (formData.proveedor.esNuevo) {
        const nuevoProveedor = await crearProveedorMutation.mutateAsync({
          nombre: formData.proveedor.nombre,
          rut: formData.proveedor.rut!,
          email: formData.proveedor.email!,
          telefono: formData.proveedor.telefono,
          categoria: formData.proveedor.categoria! as any,
          direccion: formData.proveedor.direccion,
        });

        // Actualizar los datos de la orden con el ID del proveedor creado
        datosOrden = {
          ...datosOrden,
          proveedor: {
            ...datosOrden.proveedor,
            id: nuevoProveedor.data.id,
            esNuevo: false,
          },
        };
      }

      await onSubmit(datosOrden);
      onClose();
      // Resetear formulario
      setFormData({
        proveedor: {
          nombre: '',
          esNuevo: false,
        },
        items: [],
        observaciones: '',
        fechaEntregaEsperada: '',
      });
      setPasoActual(1);
      setMostrarFormProveedor(false);
      setProveedorSeleccionado(null);
    } catch (error) {
      console.error('Error al crear orden:', error);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col overflow-hidden">
        {/* Header */}
        <div className="flex-shrink-0 p-6 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <ShoppingCart className="h-6 w-6 text-blue-600" />
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
                {ordenEditar
                  ? 'Editar Orden de Compra'
                  : 'Nueva Orden de Compra'}
              </h2>
            </div>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Progress Steps */}
          <div className="flex items-center mt-6">
            {pasos.map((paso, index) => (
              <React.Fragment key={paso.numero}>
                <div className="flex items-center">
                  <div
                    className={`flex items-center justify-center w-10 h-10 rounded-full border-2 ${
                      pasoActual >= paso.numero
                        ? 'bg-blue-600 border-blue-600 text-white'
                        : 'border-gray-300 text-gray-500'
                    }`}
                  >
                    <paso.icono className="h-5 w-5" />
                  </div>
                  <div className="ml-3">
                    <p
                      className={`text-sm font-medium ${
                        pasoActual >= paso.numero
                          ? 'text-blue-600 dark:text-blue-400'
                          : 'text-gray-500'
                      }`}
                    >
                      {paso.titulo}
                    </p>
                  </div>
                </div>
                {index < pasos.length - 1 && (
                  <div
                    className={`w-12 h-0.5 mx-4 ${
                      pasoActual > paso.numero ? 'bg-blue-600' : 'bg-gray-300'
                    }`}
                  />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 p-6 overflow-y-auto">
          {/* Paso 1: Proveedor */}
          {pasoActual === 1 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Seleccionar Proveedor
                </h3>

                {/* Opción: Seleccionar proveedor existente */}
                {!mostrarFormProveedor && (
                  <div className="space-y-4">
                    {/* Buscador de proveedores */}
                    <div className="flex items-center justify-between gap-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                        Proveedores Disponibles
                      </label>
                      <div className="relative w-64">
                        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                        <input
                          type="text"
                          placeholder="Buscar por nombre o categoría..."
                          value={busquedaProveedor}
                          onChange={(e) => setBusquedaProveedor(e.target.value)}
                          className="pl-10 pr-4 py-2 w-full text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        />
                      </div>
                    </div>

                    {cargandoProveedores ? (
                      <div className="text-center py-4">
                        <div className="inline-block animate-spin rounded-full h-6 w-6 border-b-2 border-blue-500"></div>
                        <p className="text-sm text-gray-500 mt-2">
                          Cargando proveedores...
                        </p>
                      </div>
                    ) : proveedoresDisponibles.length > 0 ? (
                      <>
                        <div className="grid grid-cols-1 gap-2 min-h-[300px]">
                          {proveedoresDisponibles.map((proveedor: any) => (
                            <div
                              key={proveedor.id}
                              onClick={() => {
                                setProveedorSeleccionado(proveedor);
                                setFormData((prev) => ({
                                  ...prev,
                                  proveedor: {
                                    id: proveedor.id,
                                    nombre: proveedor.nombre,
                                    email: proveedor.email,
                                    telefono: proveedor.telefono,
                                    rut: proveedor.rut,
                                    categoria: proveedor.categoria,
                                    esNuevo: false,
                                  },
                                }));
                              }}
                              className={`p-3 border rounded-md cursor-pointer transition-colors ${
                                proveedorSeleccionado?.id === proveedor.id
                                  ? 'border-blue-500 bg-blue-50 dark:bg-blue-900/20'
                                  : 'border-gray-300 hover:border-gray-400 dark:border-gray-600 dark:hover:border-gray-500'
                              }`}
                            >
                              <div className="flex justify-between items-start">
                                <div>
                                  <h4 className="font-medium text-gray-900 dark:text-white">
                                    {proveedor.nombre}
                                  </h4>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    {proveedor.email}
                                  </p>
                                  <p className="text-sm text-gray-500 dark:text-gray-400">
                                    RUT: {proveedor.rut}
                                  </p>
                                </div>
                                <span className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded dark:bg-gray-700 dark:text-gray-300">
                                  {proveedor.categoria}
                                </span>
                              </div>
                            </div>
                          ))}
                        </div>

                        {/* Paginación de proveedores */}
                        {totalPaginasProveedores > 1 && (
                          <div className="flex items-center justify-between border-t border-gray-200 dark:border-gray-700 pt-3">
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              Mostrando{' '}
                              {(paginaProveedores - 1) * proveedoresPorPagina +
                                1}{' '}
                              -{' '}
                              {Math.min(
                                paginaProveedores * proveedoresPorPagina,
                                totalProveedores
                              )}{' '}
                              de {totalProveedores} proveedores
                            </p>
                            <div className="flex space-x-2">
                              <button
                                onClick={() =>
                                  setPaginaProveedores((prev) =>
                                    Math.max(1, prev - 1)
                                  )
                                }
                                disabled={paginaProveedores === 1}
                                className="px-3 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>
                              <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                {paginaProveedores} / {totalPaginasProveedores}
                              </span>
                              <button
                                onClick={() =>
                                  setPaginaProveedores((prev) =>
                                    Math.min(totalPaginasProveedores, prev + 1)
                                  )
                                }
                                disabled={
                                  paginaProveedores >= totalPaginasProveedores
                                }
                                className="px-3 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                        <p className="text-sm">
                          {busquedaProveedor
                            ? `No se encontraron proveedores con "${busquedaProveedor}"`
                            : 'No hay proveedores disponibles'}
                        </p>
                      </div>
                    )}

                    {/* Botón para agregar nuevo proveedor */}
                    <div className="flex justify-center mt-4">
                      <button
                        type="button"
                        onClick={() => {
                          setMostrarFormProveedor(true);
                          setProveedorSeleccionado(null);
                          setFormData((prev) => ({
                            ...prev,
                            proveedor: {
                              nombre: '',
                              esNuevo: true,
                            },
                          }));
                        }}
                        className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-blue-700 bg-blue-100 hover:bg-blue-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-blue-900 dark:text-blue-200 dark:hover:bg-blue-800"
                      >
                        <Plus className="h-4 w-4 mr-2" />
                        Agregar Nuevo Proveedor
                      </button>
                    </div>
                  </div>
                )}

                {/* Formulario para nuevo proveedor */}
                {mostrarFormProveedor && (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Nuevo Proveedor
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setMostrarFormProveedor(false);
                          setFormData((prev) => ({
                            ...prev,
                            proveedor: {
                              nombre: '',
                              esNuevo: false,
                            },
                          }));
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nombre del Proveedor *
                        </label>
                        <input
                          type="text"
                          value={formData.proveedor.nombre}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              proveedor: {
                                ...prev.proveedor,
                                nombre: e.target.value,
                                esNuevo: true,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="Ej: Distribuidora Central"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          RUT *
                        </label>
                        <input
                          type="text"
                          value={formData.proveedor.rut || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              proveedor: {
                                ...prev.proveedor,
                                rut: e.target.value,
                                esNuevo: true,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="Ej: 12.345.678-9"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Email *
                        </label>
                        <input
                          type="email"
                          value={formData.proveedor.email || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              proveedor: {
                                ...prev.proveedor,
                                email: e.target.value,
                                esNuevo: true,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="Ej: contacto@proveedor.com"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Teléfono
                        </label>
                        <input
                          type="tel"
                          value={formData.proveedor.telefono || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              proveedor: {
                                ...prev.proveedor,
                                telefono: e.target.value,
                                esNuevo: true,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="Ej: +56 9 1234 5678"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Categoría *
                        </label>
                        <select
                          value={formData.proveedor.categoria || 'otros'}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              proveedor: {
                                ...prev.proveedor,
                                categoria: e.target.value as any,
                                esNuevo: true,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="panaderia">Panadería</option>
                          <option value="carniceria">Carnicería</option>
                          <option value="supermercado">Supermercado</option>
                          <option value="verduleria">Verdulería</option>
                          <option value="lacteos">Lácteos</option>
                          <option value="mariscos">Mariscos</option>
                          <option value="otros">Otros</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Dirección
                        </label>
                        <input
                          type="text"
                          value={formData.proveedor.direccion || ''}
                          onChange={(e) =>
                            setFormData((prev) => ({
                              ...prev,
                              proveedor: {
                                ...prev.proveedor,
                                direccion: e.target.value,
                                esNuevo: true,
                              },
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="Ej: Av. Principal 123, Santiago"
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Paso 2: Ingredientes */}
          {pasoActual === 2 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Ingredientes a Solicitar
                </h3>

                {/* Ingredientes del proveedor seleccionado */}
                {formData.proveedor.nombre && (
                  <div className="mb-6 p-4 border border-blue-200 rounded-lg bg-blue-50 dark:border-blue-700 dark:bg-blue-900/20">
                    <h4 className="text-md font-medium text-blue-900 dark:text-blue-200 mb-3">
                      Ingredientes de {formData.proveedor.nombre}
                    </h4>

                    {cargandoIngredientesProveedor ? (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                        <p className="text-sm">Cargando ingredientes...</p>
                      </div>
                    ) : ingredientesProveedor.length > 0 ? (
                      <>
                        {/* Grilla de ingredientes - 2 columnas */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                          {ingredientesProveedor
                            .slice(
                              (paginaIngredientesProveedor - 1) *
                                ingredientesPorPagina,
                              paginaIngredientesProveedor *
                                ingredientesPorPagina
                            )
                            .map((ingrediente: any) => (
                              <div
                                key={ingrediente.id}
                                className="flex items-center justify-between p-3 bg-white dark:bg-gray-800 rounded-md border border-blue-100 dark:border-blue-800"
                              >
                                <div className="flex-1 min-w-0 mr-3">
                                  <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                                    {ingrediente.nombre}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    {ingrediente.unidad_medida || 'unidad'} -{' '}
                                    {ingrediente.categoria}
                                  </p>
                                  <p className="text-xs text-gray-500 dark:text-gray-400">
                                    Stock: {ingrediente.stock_actual || 0} /
                                    Min: {ingrediente.stock_minimo || 0}
                                  </p>
                                </div>
                                <button
                                  onClick={() => {
                                    // Adaptar el formato del ingrediente
                                    const ingredienteAdaptado = {
                                      id: ingrediente.id.toString(),
                                      nombre: ingrediente.nombre,
                                      unidadMedida: {
                                        abreviacion:
                                          ingrediente.unidad_medida || 'unidad',
                                      },
                                      stockActual:
                                        parseFloat(ingrediente.stock_actual) ||
                                        0,
                                    };
                                    agregarItem(ingredienteAdaptado);
                                  }}
                                  className="flex items-center px-3 py-1 text-sm bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex-shrink-0"
                                >
                                  <Plus className="h-4 w-4 mr-1" />
                                  Agregar
                                </button>
                              </div>
                            ))}
                        </div>

                        {/* Controles de paginación */}
                        {ingredientesProveedor.length >
                          ingredientesPorPagina && (
                          <div className="flex items-center justify-between border-t border-blue-200 dark:border-blue-700 pt-3">
                            <p className="text-xs text-gray-600 dark:text-gray-400">
                              Mostrando{' '}
                              {(paginaIngredientesProveedor - 1) *
                                ingredientesPorPagina +
                                1}{' '}
                              -{' '}
                              {Math.min(
                                paginaIngredientesProveedor *
                                  ingredientesPorPagina,
                                ingredientesProveedor.length
                              )}{' '}
                              de {ingredientesProveedor.length} ingredientes
                            </p>
                            <div className="flex space-x-2">
                              <button
                                onClick={() =>
                                  setPaginaIngredientesProveedor((prev) =>
                                    Math.max(1, prev - 1)
                                  )
                                }
                                disabled={paginaIngredientesProveedor === 1}
                                className="px-3 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronLeft className="h-4 w-4" />
                              </button>
                              <span className="px-3 py-1 text-sm text-gray-700 dark:text-gray-300">
                                {paginaIngredientesProveedor} /{' '}
                                {Math.ceil(
                                  ingredientesProveedor.length /
                                    ingredientesPorPagina
                                )}
                              </span>
                              <button
                                onClick={() =>
                                  setPaginaIngredientesProveedor((prev) =>
                                    Math.min(
                                      Math.ceil(
                                        ingredientesProveedor.length /
                                          ingredientesPorPagina
                                      ),
                                      prev + 1
                                    )
                                  )
                                }
                                disabled={
                                  paginaIngredientesProveedor >=
                                  Math.ceil(
                                    ingredientesProveedor.length /
                                      ingredientesPorPagina
                                  )
                                }
                                className="px-3 py-1 text-sm bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-600 disabled:opacity-50 disabled:cursor-not-allowed"
                              >
                                <ChevronRight className="h-4 w-4" />
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    ) : (
                      <div className="text-center py-4 text-gray-500 dark:text-gray-400">
                        <Package className="h-8 w-8 mx-auto mb-2 text-gray-400" />
                        <p className="text-sm">
                          No hay ingredientes asociados a este proveedor
                        </p>
                        <p className="text-xs">
                          Puedes buscar otros ingredientes abajo o crear uno
                          nuevo
                        </p>
                      </div>
                    )}
                  </div>
                )}

                {/* Búsqueda de ingredientes */}
                <div className="mb-4">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                    <input
                      type="text"
                      placeholder="Buscar otros ingredientes..."
                      value={busquedaIngrediente}
                      onChange={(e) => setBusquedaIngrediente(e.target.value)}
                      className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  {/* Botón para crear nuevo ingrediente */}
                  <div className="mt-2 flex justify-center">
                    <button
                      type="button"
                      onClick={() => setMostrarFormIngrediente(true)}
                      className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-green-700 bg-green-100 hover:bg-green-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:bg-green-900 dark:text-green-200 dark:hover:bg-green-800"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Crear Nuevo Ingrediente
                    </button>
                  </div>
                </div>

                {/* Formulario para nuevo ingrediente */}
                {mostrarFormIngrediente && (
                  <div className="mb-4 p-4 border border-green-200 rounded-lg bg-green-50 dark:border-green-700 dark:bg-green-900/20">
                    <div className="flex items-center justify-between mb-4">
                      <h4 className="text-md font-medium text-gray-900 dark:text-white">
                        Crear Nuevo Ingrediente
                      </h4>
                      <button
                        type="button"
                        onClick={() => {
                          setMostrarFormIngrediente(false);
                          setNuevoIngrediente({
                            nombre: '',
                            descripcion: '',
                            categoria: 'otros',
                            unidad_medida: 'unidad',
                            costo_unitario: 0,
                            stock_actual: 0,
                            stock_minimo: 0,
                          });
                        }}
                        className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                      >
                        <X className="h-5 w-5" />
                      </button>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Nombre *
                        </label>
                        <input
                          type="text"
                          value={nuevoIngrediente.nombre}
                          onChange={(e) =>
                            setNuevoIngrediente((prev) => ({
                              ...prev,
                              nombre: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="Ej: Harina de trigo"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Categoría *
                        </label>
                        {!mostrandoInputCategoria ? (
                          <div className="flex gap-2">
                            <select
                              value={nuevoIngrediente.categoria}
                              onChange={(e) =>
                                setNuevoIngrediente((prev) => ({
                                  ...prev,
                                  categoria: e.target.value,
                                }))
                              }
                              className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            >
                              {categorias.map((cat) => (
                                <option key={cat.value} value={cat.value}>
                                  {cat.label}
                                </option>
                              ))}
                            </select>
                            <button
                              type="button"
                              onClick={() => setMostrandoInputCategoria(true)}
                              className="flex-shrink-0 w-10 h-10 bg-green-600 text-white rounded-md hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors flex items-center justify-center"
                              title="Agregar nueva categoría"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        ) : (
                          <div className="space-y-2">
                            <div className="flex gap-2">
                              <input
                                type="text"
                                value={nuevaCategoria}
                                onChange={(e) =>
                                  setNuevaCategoria(e.target.value)
                                }
                                placeholder="Nombre de la nueva categoría"
                                className="flex-1 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                                onKeyPress={(e) => {
                                  if (e.key === 'Enter') {
                                    e.preventDefault();
                                    agregarNuevaCategoria();
                                  }
                                  if (e.key === 'Escape') {
                                    cancelarNuevaCategoria();
                                  }
                                }}
                                autoFocus
                              />
                            </div>
                            <div className="flex gap-2 justify-end">
                              <button
                                type="button"
                                onClick={cancelarNuevaCategoria}
                                className="px-3 py-1.5 text-sm bg-gray-500 text-white rounded-md hover:bg-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-400 transition-colors"
                                title="Cancelar"
                              >
                                Cancelar
                              </button>
                              <button
                                type="button"
                                onClick={agregarNuevaCategoria}
                                disabled={!nuevaCategoria.trim()}
                                className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-green-500 transition-colors"
                                title="Confirmar nueva categoría"
                              >
                                Crear Categoría
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Unidad de Medida *
                        </label>
                        <select
                          value={nuevoIngrediente.unidad_medida}
                          onChange={(e) =>
                            setNuevoIngrediente((prev) => ({
                              ...prev,
                              unidad_medida: e.target.value,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                        >
                          <option value="unidad">Unidad</option>
                          <option value="kg">Kilogramos</option>
                          <option value="g">Gramos</option>
                          <option value="l">Litros</option>
                          <option value="ml">Mililitros</option>
                          <option value="paquete">Paquete</option>
                          <option value="caja">Caja</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Costo Unitario *
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={nuevoIngrediente.costo_unitario}
                          onChange={(e) =>
                            setNuevoIngrediente((prev) => ({
                              ...prev,
                              costo_unitario: parseFloat(e.target.value) || 0,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="0.00"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Stock Mínimo *
                        </label>
                        <input
                          type="number"
                          min="0"
                          value={nuevoIngrediente.stock_minimo}
                          onChange={(e) =>
                            setNuevoIngrediente((prev) => ({
                              ...prev,
                              stock_minimo: parseInt(e.target.value) || 0,
                            }))
                          }
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="5"
                        />
                      </div>
                      <div className="md:col-span-2">
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                          Descripción
                        </label>
                        <textarea
                          value={nuevoIngrediente.descripcion}
                          onChange={(e) =>
                            setNuevoIngrediente((prev) => ({
                              ...prev,
                              descripcion: e.target.value,
                            }))
                          }
                          rows={2}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                          placeholder="Descripción del ingrediente..."
                        />
                      </div>
                    </div>

                    <div className="mt-4 flex justify-end space-x-3">
                      <button
                        type="button"
                        onClick={() => {
                          setMostrarFormIngrediente(false);
                          setNuevoIngrediente({
                            nombre: '',
                            descripcion: '',
                            categoria: 'otros',
                            unidad_medida: 'unidad',
                            costo_unitario: 0,
                            stock_actual: 0,
                            stock_minimo: 0,
                          });
                        }}
                        className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 dark:bg-gray-700 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-600"
                      >
                        Cancelar
                      </button>
                      <button
                        type="button"
                        onClick={async () => {
                          try {
                            const datosIngrediente = {
                              ...nuevoIngrediente,
                              // Agregar proveedor como string si está disponible
                              ...(formData.proveedor.nombre && {
                                proveedor: formData.proveedor.nombre,
                              }),
                            };

                            const nuevoIngredienteCreado =
                              await crearIngredienteMutation.mutateAsync(
                                datosIngrediente
                              );

                            // Agregar automáticamente a la orden
                            const nuevoItem = {
                              id: Date.now().toString(),
                              ingredienteId:
                                nuevoIngredienteCreado.id.toString(),
                              ingredienteNombre: nuevoIngredienteCreado.nombre,
                              unidadMedida: nuevoIngrediente.unidad_medida,
                              cantidad: 1,
                              precioUnitario: nuevoIngrediente.costo_unitario,
                              subtotal: nuevoIngrediente.costo_unitario,
                            };

                            setFormData((prev) => ({
                              ...prev,
                              items: [...prev.items, nuevoItem],
                            }));

                            // Limpiar formulario y cerrar
                            setMostrarFormIngrediente(false);
                            setNuevoIngrediente({
                              nombre: '',
                              descripcion: '',
                              categoria: 'otros',
                              unidad_medida: 'unidad',
                              costo_unitario: 0,
                              stock_actual: 0,
                              stock_minimo: 0,
                            });
                          } catch (error: any) {
                            console.error('Error al crear ingrediente:', error);

                            // Manejo de errores específicos para ingredientes similares
                            if (error.response?.status === 400) {
                              // Error de ingrediente exacto activo
                              const exacto =
                                error.response.data?.ingrediente_existente ||
                                null;
                              setIngredienteDataPendiente(null); // No permitir continuar
                              setErrorModal({
                                open: true,
                                type: 'exactoActivo',
                                message:
                                  error.response.data?.error ||
                                  'Ya existe un ingrediente activo con el mismo nombre',
                                ingredientesSimilares: [],
                                ingredienteExacto: exacto,
                              });
                            } else if (error.response?.status === 409) {
                              // Error de ingredientes similares
                              const similares =
                                error.response.data?.ingredientes_similares ||
                                [];
                              // Agregar proveedor como string a los datos pendientes
                              const datosConProveedor = {
                                ...nuevoIngrediente,
                                ...(formData.proveedor.nombre && {
                                  proveedor: formData.proveedor.nombre,
                                }),
                              };
                              setIngredienteDataPendiente(datosConProveedor); // Guardar datos para continuar después
                              setErrorModal({
                                open: true,
                                type: 'similar',
                                message:
                                  error.response.data?.error ||
                                  'Se encontraron ingredientes similares',
                                ingredientesSimilares: similares,
                                ingredienteExacto: null,
                              });
                            } else if (error.response?.status === 410) {
                              // Error de ingrediente exacto inactivo
                              const exacto =
                                error.response.data?.ingrediente_exacto || null;
                              // Agregar proveedor como string a los datos pendientes
                              const datosConProveedor = {
                                ...nuevoIngrediente,
                                ...(formData.proveedor.nombre && {
                                  proveedor: formData.proveedor.nombre,
                                }),
                              };
                              setIngredienteDataPendiente(datosConProveedor); // Guardar datos para continuar después
                              setErrorModal({
                                open: true,
                                type: 'exacto',
                                message:
                                  error.response.data?.error ||
                                  'Se encontró un ingrediente inactivo con el mismo nombre',
                                ingredientesSimilares: [],
                                ingredienteExacto: exacto,
                              });
                            } else {
                              setIngredienteDataPendiente(null);
                              setErrorModal({
                                open: true,
                                type: 'simple',
                                message:
                                  'Error al crear el ingrediente. Por favor, inténtalo de nuevo.',
                                ingredientesSimilares: [],
                                ingredienteExacto: null,
                              });
                            }
                          }
                        }}
                        disabled={
                          !nuevoIngrediente.nombre ||
                          !nuevoIngrediente.categoria ||
                          crearIngredienteMutation.isPending
                        }
                        className="px-4 py-2 border border-transparent rounded-md text-sm font-medium text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {crearIngredienteMutation.isPending
                          ? 'Creando...'
                          : 'Crear y Agregar'}
                      </button>
                    </div>
                  </div>
                )}

                {/* Lista de ingredientes disponibles (filtro server-side) */}
                {busquedaIngrediente && (
                  <div className="mb-4 p-4 border border-gray-200 rounded-lg dark:border-gray-600">
                    <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ingredientes Disponibles
                    </h4>
                    <div className="space-y-2">
                      {ingredientesDisponibles.length === 0 ? (
                        <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-2">
                          {busquedaIngredienteDebounced
                            ? `Sin resultados para "${busquedaIngredienteDebounced}"`
                            : 'Buscando…'}
                        </p>
                      ) : (
                        ingredientesDisponibles.map((ingrediente: any) => (
                          <div
                            key={ingrediente.id}
                            className="flex items-center justify-between p-2 hover:bg-gray-50 dark:hover:bg-gray-700 rounded"
                          >
                            <span className="text-sm text-gray-900 dark:text-gray-100">
                              {ingrediente.nombre} (
                              {ingrediente.unidadMedida?.nombre ||
                                ingrediente.unidadMedida?.abreviacion ||
                                ingrediente.unidad_medida ||
                                'unidad'}
                              )
                            </span>
                            <button
                              onClick={() => agregarItem(ingrediente)}
                              className="text-blue-600 hover:text-blue-800 dark:text-blue-400"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                )}

                {/* Items seleccionados */}
                <div>
                  <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Items en la Orden ({formData.items.length})
                  </h4>
                  {formData.items.length === 0 ? (
                    <div className="text-center py-8 text-gray-500">
                      <Package className="h-12 w-12 mx-auto mb-2 text-gray-400" />
                      <p>No hay ingredientes agregados</p>
                      <p className="text-sm">
                        Busca y agrega ingredientes usando el campo de arriba
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {formData.items.map((item) => (
                        <div
                          key={item.id}
                          className="flex items-center gap-4 p-3 border border-gray-200 rounded-lg dark:border-gray-600"
                        >
                          <div className="flex-1">
                            <span className="font-medium text-gray-900 dark:text-gray-100">
                              {item.ingredienteNombre} ({item.unidadMedida})
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-500">
                              Cantidad:
                            </label>
                            <input
                              type="number"
                              min="1"
                              value={item.cantidad}
                              onChange={(e) =>
                                actualizarItem(
                                  item.id,
                                  'cantidad',
                                  parseFloat(e.target.value) || 1
                                )
                              }
                              className="w-20 px-2 py-1 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <label className="text-xs text-gray-500">
                              Precio:
                            </label>
                            <input
                              type="number"
                              min="0"
                              step="0.01"
                              value={item.precioUnitario}
                              onChange={(e) =>
                                actualizarItem(
                                  item.id,
                                  'precioUnitario',
                                  parseFloat(e.target.value) || 0
                                )
                              }
                              className="w-24 px-2 py-1 text-sm border border-gray-300 rounded dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                            />
                          </div>
                          <div className="text-sm font-medium text-gray-900 dark:text-gray-100 w-20 text-right">
                            {formatearMoneda(item.subtotal)}
                          </div>
                          <button
                            onClick={() => eliminarItem(item.id)}
                            className="text-red-600 hover:text-red-800 dark:text-red-400"
                          >
                            <Minus className="h-4 w-4" />
                          </button>
                        </div>
                      ))}
                      <div className="border-t border-gray-200 dark:border-gray-600 pt-3">
                        <div className="flex justify-between items-center">
                          <span className="font-medium text-gray-900 dark:text-gray-100">
                            Total:
                          </span>
                          <span className="text-lg font-bold text-gray-900 dark:text-gray-100">
                            {formatearMoneda(calcularTotal())}
                          </span>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Paso 3: Resumen */}
          {pasoActual === 3 && (
            <div className="space-y-6">
              <div>
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Resumen de la Orden
                </h3>

                {/* Información del proveedor */}
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Proveedor{' '}
                    {formData.proveedor.esNuevo && (
                      <span className="text-xs bg-green-100 text-green-800 px-2 py-1 rounded ml-2 dark:bg-green-900 dark:text-green-200">
                        Nuevo
                      </span>
                    )}
                  </h4>
                  <div className="text-sm text-gray-600 dark:text-gray-300">
                    <p>
                      <strong>Nombre:</strong> {formData.proveedor.nombre}
                    </p>
                    {formData.proveedor.rut && (
                      <p>
                        <strong>RUT:</strong> {formData.proveedor.rut}
                      </p>
                    )}
                    {formData.proveedor.email && (
                      <p>
                        <strong>Email:</strong> {formData.proveedor.email}
                      </p>
                    )}
                    {formData.proveedor.telefono && (
                      <p>
                        <strong>Teléfono:</strong> {formData.proveedor.telefono}
                      </p>
                    )}
                    {formData.proveedor.categoria && (
                      <p>
                        <strong>Categoría:</strong>{' '}
                        {formData.proveedor.categoria}
                      </p>
                    )}
                    {formData.proveedor.direccion && (
                      <p>
                        <strong>Dirección:</strong>{' '}
                        {formData.proveedor.direccion}
                      </p>
                    )}
                  </div>
                </div>

                {/* Items de la orden */}
                <div className="bg-gray-50 dark:bg-gray-700 p-4 rounded-lg mb-4">
                  <h4 className="font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Items ({formData.items.length})
                  </h4>
                  <div className="space-y-2">
                    {formData.items.map((item) => (
                      <div
                        key={item.id}
                        className="flex justify-between text-sm"
                      >
                        <span className="text-gray-600 dark:text-gray-300">
                          {item.ingredienteNombre} ({item.unidadMedida}) x{' '}
                          {item.cantidad}
                        </span>
                        <span className="font-medium text-gray-900 dark:text-gray-100">
                          {formatearMoneda(item.subtotal)}
                        </span>
                      </div>
                    ))}
                    <div className="border-t border-gray-200 dark:border-gray-600 pt-2 mt-2">
                      <div className="space-y-1">
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            Subtotal:
                          </span>
                          <span className="text-gray-900 dark:text-gray-100">
                            {formatearMoneda(
                              calcularTotalesConDesglose().subtotal
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span className="text-gray-600 dark:text-gray-400">
                            IVA (19%):
                          </span>
                          <span className="text-gray-900 dark:text-gray-100">
                            {formatearMoneda(
                              calcularTotalesConDesglose().impuesto
                            )}
                          </span>
                        </div>
                        <div className="flex justify-between font-bold text-lg border-t border-gray-200 dark:border-gray-600 pt-1">
                          <span className="text-gray-900 dark:text-gray-100">
                            Total:
                          </span>
                          <span className="text-gray-900 dark:text-gray-100">
                            {formatearMoneda(
                              calcularTotalesConDesglose().total
                            )}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Detalles adicionales */}
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Fecha de Entrega Esperada
                    </label>
                    <input
                      type="date"
                      value={formData.fechaEntregaEsperada}
                      min={new Date().toISOString().split('T')[0]} // No permitir fechas anteriores a hoy
                      onChange={(e) => {
                        const selectedDate = e.target.value;
                        const today = new Date().toISOString().split('T')[0];

                        if (selectedDate < today) {
                          alert(
                            'La fecha de entrega no puede ser anterior a hoy'
                          );
                          return;
                        }

                        setFormData((prev) => ({
                          ...prev,
                          fechaEntregaEsperada: selectedDate,
                        }));
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Observaciones
                    </label>
                    <textarea
                      value={formData.observaciones}
                      onChange={(e) =>
                        setFormData((prev) => ({
                          ...prev,
                          observaciones: e.target.value,
                        }))
                      }
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                      placeholder="Observaciones adicionales para la orden..."
                    />
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex-shrink-0 px-6 py-4 border-t border-gray-200 dark:border-gray-700 flex justify-between">
          <button
            onClick={pasoAnterior}
            disabled={pasoActual === 1}
            className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500"
          >
            <ChevronLeft className="h-4 w-4" />
            Anterior
          </button>

          <div className="flex gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 border border-gray-300 rounded-md hover:bg-gray-200 dark:bg-gray-600 dark:text-gray-300 dark:border-gray-500 dark:hover:bg-gray-500"
            >
              Cancelar
            </button>

            {pasoActual < totalPasos ? (
              <button
                onClick={siguientePaso}
                disabled={!validarPaso(pasoActual)}
                className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-blue-600 border border-transparent rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Siguiente
                <ChevronRight className="h-4 w-4" />
              </button>
            ) : (
              <button
                onClick={handleSubmit}
                disabled={loading || !validarPaso(pasoActual)}
                className="px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Creando...' : 'Crear Orden'}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Modal de error para ingredientes similares */}
      <ErrorModal
        isOpen={errorModal.open}
        type={errorModal.type}
        message={errorModal.message}
        ingredientesSimilares={errorModal.ingredientesSimilares}
        ingredienteExacto={errorModal.ingredienteExacto}
        onClose={() => setErrorModal((prev) => ({ ...prev, open: false }))}
        onReactivarIngrediente={handleReactivarIngrediente}
        onSeleccionarSimilar={handleSeleccionarSimilar}
        onContinuarDeTodasFormas={handleContinuarDeTodasFormas}
      />
    </div>
  );
};

export default WizardOrdenCompra;
